/*
 * The PRODUCT filter box, shared by the browse page and the release-note list.
 *
 * The owner asked for the release-note list to "re-use the PRODUCT filter box"
 * (S42, 2026-10-12), so it is the same box rather than a look-alike: extracted
 * from BrowsePage with its markup unchanged - a tight card holding an h3
 * "Product", the one-line rule, and a checkbox list named "products" - so the
 * browse page renders exactly what it rendered before (App.test pins its heading
 * outline) and a change to the box lands on both pages at once.
 *
 * What it does NOT take from the browse page is where its options come from.
 * The browse page feeds it the bootstrap catalogue, which is the ARTICLE facet:
 * products with live articles, counted by articles. The release-note list feeds
 * it the facet its own response carries - products with live release notes,
 * counted by release notes - because the article facet would hide a product that
 * only has release notes and print the wrong numbers beside the rest. So the
 * options are a parameter, and so are the counts.
 *
 * Selection is a list of ids and the caller owns it, along with how it travels
 * (both pages write it to `?products=`): the box only reports the next list.
 */

import { CheckboxList } from '../components/ui.js';
import { useMessages } from '../i18n/language.js';

/** One option in the box: a product id, the name to print, and how many items carry it. */
export interface ProductFilterItem {
  id: number;
  label: string;
  /** Omitted, no count is printed. */
  count?: number;
}

/**
 * The PRODUCT box. Within the box the ticked products are OR-ed ("matches any
 * selected product"), and it says so.
 */
export function ProductFilter({
  items,
  selected,
  onChange,
}: {
  items: ProductFilterItem[];
  selected: number[];
  onChange: (next: number[]) => void;
}): JSX.Element {
  const t = useMessages();

  return (
    <div className="sidebar-group card card-tight">
      <h3>{t.filters.product}</h3>
      <p className="faint" style={{ margin: '0 0 0.5rem' }}>
        {t.filters.productRule}
      </p>
      <CheckboxList name="products" selected={selected} onChange={onChange} items={items} />
    </div>
  );
}
